// utils/cryptoStorage.js
// NOTE: This is obfuscation, not real security. The key is embedded in the
// frontend bundle, so it will not stop XSS-based token theft. This is a
// stopgap until tokens are moved to httpOnly cookies.

const ENC_KEY_STRING = "change-this-to-your-own-passphrase"; // move to env var at least
const SALT = "static-salt-for-this-app"; // fixed salt is fine here since this isn't meant to be cryptographically strong

async function getKey() {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(ENC_KEY_STRING),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode(SALT),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

function bufToBase64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function base64ToBuf(base64) {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

export async function encryptData(data) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const encoded = enc.encode(JSON.stringify(data));

  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );

  // Store iv alongside ciphertext, both base64-encoded, joined with a separator
  return `${bufToBase64(iv)}.${bufToBase64(ciphertext)}`;
}

export async function decryptData(payload) {
  if (!payload) return null;

  try {
    const [ivB64, dataB64] = payload.split(".");
    if (!ivB64 || !dataB64) return null;

    const key = await getKey();
    const iv = base64ToBuf(ivB64);
    const ciphertext = base64ToBuf(dataB64);

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    const dec = new TextDecoder();
    return JSON.parse(dec.decode(decrypted));
  } catch (err) {
    console.error("Failed to decrypt storage payload:", err);
    return null;
  }
}

export async function setEncryptedItem(key, value) {
  const encrypted = await encryptData(value);
  localStorage.setItem(key, encrypted);
}

export async function getDecryptedItem(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  return decryptData(raw);
}

export function removeEncryptedItem(key) {
  localStorage.removeItem(key);
}